

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import activeIcon from '../assect/icons/map-marker-active.png';
import inactiveIcon from '../assect/icons/map-marker-inactive.png';
import { InfoWindow } from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
const MarkerLayer = ({ markers, selectedMapCoords }) => {
  const navigate = useNavigate()
  const [hoveredKey, setHoveredKey] = useState(null);
  const [selectedClusterIndex, setSelectedClusterIndex] = useState(null);
  const map = useMap();
  const isSelected = (lat, lng) => {
    // Don't consider coordinates as selected if they are zero
    if (selectedMapCoords?.lat === 0 && selectedMapCoords?.lng === 0) {
      return false;
    }
    return selectedMapCoords?.lat === lat && selectedMapCoords?.lng === lng;
  };

  // Group markers by lat/lng coordinates
  const groupedMarkers = useMemo(() => {
    const groups = {};

    markers.forEach((marker, index) => {
      const key = `${marker.location.lat}_${marker.location.lng}`;
      if (!groups[key]) {
        groups[key] = {
          location: marker.location,
          markers: [],
          isSelected: false
        };
      }
      groups[key].markers.push({ ...marker, originalIndex: index });
    });

    // Check if any marker in each group is selected
    Object.keys(groups).forEach(key => {
      groups[key].isSelected = groups[key].markers.some(marker =>
        isSelected(marker.location.lat, marker.location.lng)
      );
    });

    return Object.values(groups);
  }, [markers, selectedMapCoords]);

  // Smooth transition to selected marker
  const smoothPanTo = (map, targetLatLng, duration = 1000) => {
    if (!map) return;

    const start = map.getCenter();
    const startLat = start.lat();
    const startLng = start.lng();
    const endLat = targetLatLng.lat;
    const endLng = targetLatLng.lng;
    const deltaLat = endLat - startLat;
    const deltaLng = endLng - startLng;

    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / duration;

      if (progress < 1) {
        const lat = startLat + deltaLat * progress;
        const lng = startLng + deltaLng * progress;
        map.setCenter({ lat, lng });
        requestAnimationFrame(step);
      } else {
        map.setCenter(targetLatLng);
      }
    };

    requestAnimationFrame(step);
  };

  useEffect(() => {
    if (map && selectedMapCoords?.lat && selectedMapCoords?.lng) {
      smoothPanTo(map, selectedMapCoords, 700);
    }
  }, [selectedMapCoords, map]);


  const handleMarkerMouseEnter = (groupIndex) => {
    setHoveredKey(groupIndex);
  };

  const handleClusterClick = (groupIndex) => {
    if (groupedMarkers[groupIndex].markers.length > 0) {
      setSelectedClusterIndex(selectedClusterIndex === groupIndex ? null : groupIndex);
    }
  };

  useEffect(() => {
    // Only set hovered key if coordinates are not zero
    if (selectedMapCoords?.lat !== 0 || selectedMapCoords?.lng !== 0) {
      if (selectedMapCoords?.address) {
        // Find the group that contains the selected marker
        const groupIndex = groupedMarkers.findIndex(group =>
          group.markers.some(marker => marker.key === selectedMapCoords.address)
        );
        if (groupIndex !== -1) {
          handleMarkerMouseEnter(groupIndex);
        }
      }
    } else {
      // Clear hovered key when coordinates are reset to zero
      setHoveredKey(null);
    }
  }, [selectedMapCoords, groupedMarkers]);


  const handleMarkerMouseLeave = () => {
    // Add a small delay to prevent flickering when moving to InfoWindow
    setTimeout(() => {
      setHoveredKey(null);
    }, 100);
  };

  // Create custom marker with count
  const createMarkerElement = (group, groupIndex) => {
    const markerCount = group.markers.length;
    const isSelectedGroup = group.isSelected;
    const isHovered = hoveredKey === groupIndex;

    if (markerCount === 1) {
      // Single marker - use original icon
      return (
        <img
          src={isSelectedGroup ? activeIcon : inactiveIcon}
          alt="Map Marker"
          className={isSelectedGroup ? 'marker-pulse' : ''}
          style={{
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)'
          }}
        />
      );
    } else {
      // Multiple markers - create clustered marker
      return (
        <div
          style={{
            position: 'relative',
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)'
          }}
        >
          <img
            src={isSelectedGroup ? activeIcon : inactiveIcon}
            alt="Map Marker"
            className={isSelectedGroup ? 'marker-pulse' : ''}
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: isSelectedGroup ? '#ff4444' : '#2196F3',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}
          >
            {markerCount}
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {groupedMarkers.map((group, groupIndex) => {
        const isHovered = hoveredKey === groupIndex;
        const isClusterExpanded = selectedClusterIndex === groupIndex;
        const markerCount = group.markers.length;

        return (
          <div key={`group-${groupIndex}`}>
            <AdvancedMarker position={group.location}>
              <div
                onMouseEnter={() => handleMarkerMouseEnter(groupIndex)}
                onMouseLeave={handleMarkerMouseLeave}
                onClick={() => handleClusterClick(groupIndex)}
              >
                {createMarkerElement(group, groupIndex)}
              </div>
            </AdvancedMarker>

            {/* Show cluster info window for multiple markers */}
            {isHovered && markerCount > 0 && !isClusterExpanded && (
              <InfoWindow
                position={group.location}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -40),
                  disableAutoPan: true,
                  disableCloseButton: true
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    minWidth: '160px',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  <strong style={{ color: '#333', fontSize: '14px' }}>
                    {markerCount === 1 ? (
                      `${markerCount} Property at this location`
                    ) : (
                      `${markerCount} Properties at this location`
                    )}
                  </strong>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
                    {markerCount === 1 ? (
                      'Click to see this property'
                    ) : (
                      'Click to see all properties'
                    )}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#888' }}>
                    {
                      markerCount === 1 ? (`Price: $${group.markers.map(m => m.price).toLocaleString()}`) : (`  Price range: $${Math.min(...group.markers.map(m => m.price)).toLocaleString()} - $${Math.max(...group.markers.map(m => m.price)).toLocaleString()}`)
                    }

                  </div>
                </div>
              </InfoWindow>
            )}

            {/* Show expanded cluster info window */}
            {isClusterExpanded && (
              <InfoWindow
                position={group.location}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -40),
                  disableAutoPan: true,
                  disableCloseButton: true
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    maxWidth: '300px',
                    fontFamily: 'Arial, sans-serif',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <strong style={{ color: '#333', fontSize: '14px' }}>
                      {markerCount === 1 ? (
                        `${markerCount} Property`
                      ) : (
                        `${markerCount} Properties`
                      )}
                      
                    </strong>
                    <button
                      onClick={() => setSelectedClusterIndex(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {group.markers.map((marker, index) => {
                    return <div
                      key={`${marker.key}-${index}`}
                      className="py-2 border-bottom border-light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/my-property-detail/${marker.id}`)}
                    >
                      <strong className="d-block text-dark">{marker.key}</strong>

                      <div className="row mt-1 gx-2">
                        <div className="col-auto">
                          <span className="text-muted">🛏️ {marker.beds} beds</span>
                        </div>
                        <div className="col-auto">
                          <span className="text-muted">🛁 {marker.baths} baths</span>
                        </div>
                      </div>

                      <div className="row mt-1">
                        <div className="col-12">
                          <span className="text-muted">🏙️ {marker.city}</span>
                        </div>
                      </div>

                      <div className="row mt-1">
                        <div className="col-12">
                          <span className="text-dark fw-semibold">💲 {marker.price.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  })}
                </div>
              </InfoWindow>
            )}
          </div>
        );
      })}
    </>
  );
};

const MapComponent = ({ markers, selectedMapCoords }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;
  const defaultCenter = { lat: 42.7284, lng: -73.6918 };
  const zoom = 14;
  const [markerPositions, setMarkerPositions] = useState([]);

  useEffect(() => {
    if (Array.isArray(markers) && markers.length > 0) {
      setMarkerPositions(markers);
    }
  }, [markers]);

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        mapId={mapId}
        style={{ width: "100%" }}
        className="youtube_video"
        defaultCenter={defaultCenter}
        defaultZoom={zoom}
      >
        <MarkerLayer
          markers={markerPositions}
          selectedMapCoords={selectedMapCoords}
        />
      </Map>
    </APIProvider>
  );
};

export default MapComponent;